import { Box, Button, Chip, Fab, Paper, Stack, Typography } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import React from "react";

import MedicalUxVersion from "../Types/MedicalUxVersion";

const experienceLabels: Record<MedicalUxVersion, string> = {
  [MedicalUxVersion.CombinedPage]: "All questions on one page",
  [MedicalUxVersion.OneAtATime]: "Auto-Advance Single Question",
  [MedicalUxVersion.AutoScroll]: "All questions with auto-scroll"
};

export interface IMedicalWireframePickerProps {
  onSelect: (version: MedicalUxVersion) => void;
}

/** Inline selection card rendered in place of the medical step when no wireframe UX version has been chosen. */
const MedicalWireframePicker: React.FC<IMedicalWireframePickerProps> = ({ onSelect }) => (
  <Box display="flex" justifyContent="center" alignItems="center" width="100%" height="100%">
    <Stack spacing={1} sx={{ maxWidth: 480, width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: "warning.light",
          border: "1px dashed",
          borderColor: "warning.main",
          borderRadius: 1
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label="PROTOTYPE" size="small" color="warning" sx={{ fontWeight: 700, letterSpacing: 1 }} />
          <Typography variant="caption" color="warning.dark" fontWeight={600}>
            This screen is not part of the production build. It is used for internal wireframe review only.
          </Typography>
        </Stack>
      </Paper>
      <Paper elevation={3} sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Select a UX Variant</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose a layout to preview. Refreshing the page will return to this screen.
          </Typography>
          <Stack spacing={0.75}>
            {Object.values(MedicalUxVersion).map(version => (
              <Button key={version} variant="contained" fullWidth onClick={() => onSelect(version)}>
                {experienceLabels[version]}
              </Button>
            ))}
          </Stack>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Fab
              size="small"
              color="secondary"
              disabled
              sx={{ flexShrink: 0, "&.Mui-disabled": { bgcolor: "secondary.main", color: "secondary.contrastText" } }}
            >
              <RestartAltIcon />
            </Fab>
            <Typography variant="caption" color="text.secondary">
              Once inside an experience, tap this button (bottom-right corner) to clear your answers and return to this
              screen.
            </Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  </Box>
);

export default MedicalWireframePicker;
